import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import toast from 'react-hot-toast';
import { api } from '../lib/api';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

export default function ScenariosPage() {
  const [scenarios, setScenarios] = useState([]);
  const [categories, setCategories] = useState([]);
  const [forecasts, setForecasts] = useState({});  // scenarioId -> forecast data
  const [activeScenarios, setActiveScenarios] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', adjustments: [] });
  const year = new Date().getFullYear();

  const load = async () => {
    const [sc, cats] = await Promise.all([api.getScenarios(), api.getCategories()]);
    setScenarios(sc);
    setCategories(cats);
  };

  useEffect(() => { load(); }, []);

  const loadForecast = async (scenario) => {
    if (forecasts[scenario.id]) return;
    try {
      const fc = await api.getForecast(year, scenario.adjustments);
      setForecasts(f => ({ ...f, [scenario.id]: fc }));
    } catch (e) {
      toast.error('Could not load forecast for scenario');
    }
  };

  const loadBaseForecast = async () => {
    if (forecasts['base']) return;
    const fc = await api.getForecast(year);
    setForecasts(f => ({ ...f, base: fc }));
  };

  const toggleScenario = async (scenario) => {
    const isActive = activeScenarios.includes(scenario.id);
    if (isActive) {
      setActiveScenarios(a => a.filter(id => id !== scenario.id));
    } else {
      await loadForecast(scenario);
      await loadBaseForecast();
      setActiveScenarios(a => [...a, scenario.id]);
    }
  };

  const saveScenario = async () => {
    try {
      await api.createScenario(form);
      toast.success('Scenario saved!');
      setShowModal(false);
      setForm({ name: '', description: '', adjustments: [] });
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const deleteScenario = async (id) => {
    if (!window.confirm('Delete this scenario?')) return;
    await api.deleteScenario(id);
    setScenarios(s => s.filter(x => x.id !== id));
    setActiveScenarios(a => a.filter(x => x !== id));
    toast.success('Deleted');
  };

  const addAdjustment = () => {
    setForm(f => ({
      ...f,
      adjustments: [...f.adjustments, { category: '', delta: 0, start_month: 1, end_month: 12 }]
    }));
  };

  const updateAdj = (i, key, val) => {
    setForm(f => {
      const adj = [...f.adjustments];
      adj[i] = { ...adj[i], [key]: val };
      return { ...f, adjustments: adj };
    });
  };

  // Build comparison chart data
  const comparisonData = (() => {
    if (!forecasts.base) return [];
    const base = forecasts.base.months;
    return base.map((m, i) => {
      const row = { name: m.month_name, Base: m.cumulative };
      activeScenarios.forEach(sid => {
        const sc = scenarios.find(s => s.id === sid);
        if (forecasts[sid]) row[sc.name] = forecasts[sid].months[i].cumulative;
      });
      return row;
    });
  })();

  const COLORS = ['var(--accent)', 'var(--blue)', 'var(--purple)', 'var(--green)'];

  return (
    <div className="animate-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="page-title" style={{ marginBottom: 2 }}>Scenarios</div>
          <div className="page-subtitle" style={{ marginBottom: 0 }}>Model "what if" situations and compare year-end outcomes</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ New Scenario</button>
      </div>

      {/* Scenario cards */}
      <div className="grid-2 mb-6">
        {scenarios.length === 0 && (
          <div className="card" style={{ borderStyle: 'dashed', textAlign: 'center', padding: '40px', gridColumn: '1/-1' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>⊕</div>
            <div style={{ color: 'var(--text-secondary)', marginBottom: 6 }}>No scenarios yet</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>Create scenarios like "Cut dining by $200/mo" or "Baby year 2 daycare increase"</div>
          </div>
        )}
        {scenarios.map(sc => {
          const isActive = activeScenarios.includes(sc.id);
          const fc = forecasts[sc.id];
          const base = forecasts.base;
          const yearEndDelta = (fc && base) ? fc.year_end_balance - base.year_end_balance : null;

          return (
            <div key={sc.id} className="card" style={{ border: isActive ? '1px solid var(--accent)' : '' }}>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{sc.name}</div>
                  {sc.description && <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>{sc.description}</div>}
                </div>
                <div className="flex gap-2">
                  <button className={`btn btn-sm ${isActive ? 'btn-primary' : 'btn-ghost'}`} onClick={() => toggleScenario(sc)}>
                    {isActive ? 'Active' : 'Compare'}
                  </button>
                  <button className="btn btn-sm btn-danger" onClick={() => deleteScenario(sc.id)}>×</button>
                </div>
              </div>

              {sc.adjustments.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {sc.adjustments.map((adj, i) => (
                    <div key={i} className="flex justify-between" style={{ fontSize: 13, padding: '6px 10px', background: 'var(--bg-elevated)', borderRadius: 6 }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{adj.category}</span>
                      <span style={{ color: adj.delta >= 0 ? 'var(--red)' : 'var(--green)', fontWeight: 500 }}>
                        {adj.delta >= 0 ? '+' : ''}{fmt(adj.delta)}/mo
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {yearEndDelta !== null && (
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Year-end impact: </span>
                  <span style={{ fontWeight: 600, color: yearEndDelta >= 0 ? 'var(--green)' : 'var(--red)' }}>
                    {yearEndDelta >= 0 ? '+' : ''}{fmt(yearEndDelta)}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Comparison chart */}
      {comparisonData.length > 0 && (
        <div className="card">
          <div className="section-title mb-4">Cumulative Balance Comparison — {year}</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={comparisonData}>
              <XAxis dataKey="name" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip
                contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-bright)', borderRadius: 8 }}
                labelStyle={{ color: 'var(--text-muted)', fontSize: 11 }}
                formatter={(v) => fmt(v)}
              />
              <Legend wrapperStyle={{ fontSize: 12, color: 'var(--text-secondary)' }} />
              {['Base', ...activeScenarios.map(id => scenarios.find(s => s.id === id)?.name)].filter(Boolean).map((key, i) => (
                <Bar key={key} dataKey={key} fill={COLORS[i % COLORS.length]} radius={[4,4,0,0]} maxBarSize={22} opacity={0.85} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Create modal */}
      {showModal && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-title">New Scenario</div>
            <div className="form-group">
              <label className="form-label">Name</label>
              <input className="input" placeholder='e.g. "Cut dining by 30%"' value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Description (optional)</label>
              <input className="input" placeholder="What does this scenario model?" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>

            <div className="section-title mt-4 mb-3">Monthly Adjustments</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 240, overflowY: 'auto', marginBottom: 12 }}>
              {form.adjustments.map((adj, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select className="input" style={{ flex: 2 }} value={adj.category} onChange={e => updateAdj(i, 'category', e.target.value)}>
                    <option value="">Select category</option>
                    {categories.map(c => <option key={c.id}>{c.name}</option>)}
                  </select>
                  <input className="input" type="number" style={{ flex: 1 }} placeholder="±$/mo" value={adj.delta}
                    onChange={e => updateAdj(i, 'delta', parseFloat(e.target.value) || 0)} />
                  <button className="btn btn-sm btn-danger" onClick={() => setForm(f => ({ ...f, adjustments: f.adjustments.filter((_, j) => j !== i) }))}>×</button>
                </div>
              ))}
            </div>
            <button className="btn btn-ghost btn-sm mb-4" onClick={addAdjustment}>+ Add adjustment</button>

            <div className="flex gap-3 justify-between">
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveScenario} disabled={!form.name}>Save Scenario</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
